import {
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useRef,
  useState,
} from 'react';

const LONG_PRESS_MS = 480;
const HOLD_HIGHLIGHT_MS = 140;
const MOVE_CANCEL_PX = 12;

export type LongPressOrigin = {
  x: number;
  y: number;
};

/**
 * Opens a menu on touch/pen long-press or mouse right-click.
 * Suppresses the click that iOS fires after a long-press.
 * Passes the press/click origin so callers can anchor a floating panel.
 */
export function useLongPressMenu(onOpen: (origin: LongPressOrigin) => void) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startRef = useRef<LongPressOrigin | null>(null);
  const suppressClickRef = useRef(false);
  const [isHolding, setIsHolding] = useState(false);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    };
  }, []);

  const clearTimers = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (highlightTimerRef.current) {
      clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }
    startRef.current = null;
  };

  const cancelHold = () => {
    clearTimers();
    setIsHolding(false);
  };

  const openMenu = (origin: LongPressOrigin) => {
    clearTimers();
    setIsHolding(false);
    suppressClickRef.current = true;
    onOpen(origin);
  };

  return {
    isHolding,
    onContextMenu: (event: {
      preventDefault: () => void;
      clientX: number;
      clientY: number;
    }) => {
      event.preventDefault();
      openMenu({ x: event.clientX, y: event.clientY });
    },
    onPointerDown: (event: ReactPointerEvent) => {
      if (event.pointerType === 'mouse') return;
      startRef.current = { x: event.clientX, y: event.clientY };
      highlightTimerRef.current = setTimeout(() => {
        setIsHolding(true);
      }, HOLD_HIGHLIGHT_MS);
      timerRef.current = setTimeout(() => {
        const origin = startRef.current ?? {
          x: event.clientX,
          y: event.clientY,
        };
        openMenu(origin);
      }, LONG_PRESS_MS);
    },
    onPointerMove: (event: ReactPointerEvent) => {
      if (!startRef.current || !timerRef.current) return;
      const dx = event.clientX - startRef.current.x;
      const dy = event.clientY - startRef.current.y;
      if (dx * dx + dy * dy > MOVE_CANCEL_PX * MOVE_CANCEL_PX) {
        cancelHold();
      }
    },
    onPointerUp: cancelHold,
    onPointerCancel: clearTimers,
    consumeClickIfMenuOpened: () => {
      if (!suppressClickRef.current) return false;
      suppressClickRef.current = false;
      return true;
    },
  };
}
